import { ApplicationInsights, ITelemetryItem } from "@microsoft/applicationinsights-web";
import { NgSession } from "@sazka/ng/model/ng-fw";
import { Ref } from "vue";

const toTrack = [
    "trackEvent",
    "trackPageView",
    "trackPageViewPerformance",
    "trackException",
    "trackTrace",
    "trackMetric",
    "trackDependencyData",
    "throwInternal", // called when a message is logged internally
    "logInternalMessage", // called when a message is logged internally
    "triggerSend", // called when data is queued to be sent to the server
    "_sender", // called when data is sent to the server
];

let appInsights: ApplicationInsights = null;

const isDev = environment.env === "Development";

let ngSessionRef: Ref<NgSession> = null;

async function init() {
    const extensions = [];
    const extensionConfig = {};

    if (!environment.isKasa) {
        const { useNgSession } = await import("@sazka/ng/ng-session");
        ngSessionRef = useNgSession().session;
    }

    if (isDev) {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const { DebugPlugin } = await import("@microsoft/applicationinsights-debugplugin-js");
        const debugPluginInstance = new DebugPlugin();
        extensions.push(debugPluginInstance);
        extensionConfig[DebugPlugin.identifier] = {
            trackers: toTrack,
        };
    }

    appInsights = new ApplicationInsights({
        config: {
            connectionString: appSettings["APPLICATIONINSIGHTS_CONNECTION_STRING"],
            disableAjaxTracking: true,
            disableFetchTracking: true,
            disableDataLossAnalysis: false,
            disablePageUnloadEvents: ["unload"],
            enableUnhandledPromiseRejectionTracking: true,
            extensions: extensions,
            extensionConfig: extensionConfig,
        },
    });

    const telemetryInitializer = (envelope: ITelemetryItem) => {
        envelope.data = envelope.data || {};
        envelope.data.playerId = ngSessionRef?.value?.PlayerID ?? null;
        envelope.data.location = window.location.href;
        envelope.data.userAgent = window.navigator.userAgent;
        envelope.data.mobileView = environment.isInAppView;
    };

    appInsights.addTelemetryInitializer(telemetryInitializer);
    appInsights.loadAppInsights();
}

function trackEvent(name: string, properties: Record<string, any>) {
    appInsights?.trackEvent(
        {
            name: name,
        },
        {
            properties,
        }
    );
}

// Used to track exceptions with custom properties in addition to automatically tracked exceptions (other than base data).
function trackException(exception: Error, properties: Record<string, any> = {}) {
    appInsights?.trackException({ exception, properties });
}

function trackMetric(name: string, value: number) {
    appInsights?.trackMetric({ name: name, average: value });
}

export function useAppInsights() {
    return {
        init,
        trackEvent,
        trackMetric,
        trackException,
    };
}
